// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { SectorMap } from '@shared/commons/Heatmap/Maps/SectorMap';
import { HeatmapAreaBaseRenderer } from './HeatmapAreaBaseRenderer';
import { Rectangle } from '@shared/commons/Geometry';
import { Pen, SolidBrush, Font } from '@shared/commons/Graphics';
import { ThemeManager } from '../../../misc/ThemeManager';
export class HeatmapSectorRenderer extends HeatmapAreaBaseRenderer {
    constructor(sectorMap, settings) {
        super();
        this._allText = 'All';
        this._arrowText = '>';
        this._undefinedIndustryName = 'Undefined industry';
        this._undefinedSectorName = 'Undefined sector';
        this.headerAllRectangle = Rectangle.Empty();
        this.headerAllSignRectangle = Rectangle.Empty();
        this.headerIndustryTextRectangle = Rectangle.Empty();
        this.sectorMap = sectorMap;
        this.areaName = isValidString(sectorMap.sectorName)
            ? sectorMap.sectorName.toUpperCase()
            : this._undefinedSectorName.toUpperCase();
        this.settings = settings;
    }
    draw(gr, mousePoint) {
        if (!this.sectorMap.Visible) {
            return;
        }
        this.setRectangles(gr);
        const backgroundBrush = new SolidBrush(this.settings.sectorLevelBackgroundColor);
        gr.FillRectWithRect(backgroundBrush, this.sectorMap.Rectangle);
        if (!this.headerAllRectangle.IsEmpty()) {
            this.drawHeaderSelector(gr, mousePoint);
        }
        else {
            const font = new Font(ThemeManager.getMediumFontSize(), ThemeManager.customFontFamily);
            const isHovered = this.isHeaderHovered(mousePoint);
            const headerBackgroundBrush = new SolidBrush(isHovered
                ? ThemeManager.colors.heatmapSectorLevelBackgroundHovered
                : this.settings.sectorLevelBackgroundColor);
            const headerForegroundBrush = new SolidBrush(isHovered ? ThemeManager.colors.heatmapSectorLevelForegroundHovered : this.settings.sectorLevelFontColor);
            const sectorName = this.areaName;
            gr.FillRectWithRect(headerBackgroundBrush, this.sectorMap.headerRectangle);
            this.drawHeaderText(gr, sectorName, font, headerForegroundBrush, this.headerTextRectangle);
        }
    }
    drawSelection(gr, mousePoint) {
        if (!this.sectorMap.Visible) {
            return;
        }
        if (this.isHeaderHovered(mousePoint) && this.headerAllRectangle.IsEmpty()) {
            const rect = this.sectorMap.Rectangle;
            const strokeLineWidth = HeatmapSectorRenderer.HOVERWIDTH;
            const strokeOffset = strokeLineWidth / 2;
            gr.strokeStyle = ThemeManager.colors.HeatmapHeaderBackgroundHovered;
            gr.lineWidth = strokeLineWidth;
            gr.strokeRect(rect.X + SectorMap.BORDER + strokeOffset, rect.Y + SectorMap.BORDER + strokeOffset, rect.Width - strokeLineWidth - 2 * SectorMap.BORDER, rect.Height - strokeLineWidth - 2 * SectorMap.BORDER);
        }
    }
    drawHeaderSelector(gr, mousePoint) {
        const font = new Font(ThemeManager.getMediumFontSize(), ThemeManager.customFontFamily);
        const foregroundDefaultBrush = new SolidBrush(this.settings.sectorLevelFontColor);
        const foregroundHoveredBrush = new SolidBrush(ThemeManager.colors.HeatmapPanelNavigationHoveredForegroundColor);
        const backgroundHoveredBrush = new SolidBrush(ThemeManager.colors.HeatmapPanelNavigationHoveredBackgroundColor);
        const borderHoveredPen = new Pen(ThemeManager.colors.HeatmapPanelNavigationHoveredBorderColor);
        if (this.isAllHovered(mousePoint)) {
            const allRect = this.headerAllRectangle;
            gr.RoundRectangle(allRect, 4, backgroundHoveredBrush, borderHoveredPen);
            gr.DrawStringInRect(this._allText, font, foregroundHoveredBrush, allRect, false);
        }
        else {
            gr.DrawStringInRect(this._allText, font, foregroundDefaultBrush, this.headerAllRectangle, false);
        }
        gr.DrawStringInRect(this._arrowText, font, foregroundDefaultBrush, this.headerAllSignRectangle, false);
        const sectorName = this.areaName;
        const selectedIndustry = this.sectorMap.selectedIndustry;
        if (this.isSectorNameHovered(mousePoint) && !isNullOrUndefined(selectedIndustry)) {
            const sectorRect = this.headerTextRectangle;
            gr.RoundRectangle(sectorRect, 4, backgroundHoveredBrush, borderHoveredPen);
            this.drawHeaderText(gr, sectorName, font, foregroundHoveredBrush, this.headerTextRectangle, 'center');
        }
        else {
            this.drawHeaderText(gr, sectorName, font, foregroundDefaultBrush, this.headerTextRectangle, 'center');
        }
        if (!this.selectorSignRectangle.IsEmpty()) {
            gr.DrawStringInRect(this._arrowText, font, foregroundDefaultBrush, this.selectorSignRectangle, false);
        }
        if (!this.headerIndustryTextRectangle.IsEmpty() && !isNullOrUndefined(selectedIndustry)) {
            gr.DrawStringInRect(this.getSelectedIndustryName(), font, foregroundDefaultBrush, this.headerIndustryTextRectangle, false);
        }
    }
    isHeaderHovered(mousePoint) {
        return !mousePoint.IsEmpty() && this.sectorMap.headerRectangle.Contains(mousePoint.X, mousePoint.Y);
    }
    isAllHovered(mousePoint) {
        return !mousePoint.IsEmpty() && this.headerAllRectangle.Contains(mousePoint.X, mousePoint.Y);
    }
    isSectorNameHovered(mousePoint) {
        return (!mousePoint.IsEmpty() &&
            !this.headerAllRectangle.IsEmpty() &&
            this.headerTextRectangle.Contains(mousePoint.X, mousePoint.Y));
    }
    setRectangles(gr) {
        const fontSize = ThemeManager.getMediumFontSize();
        const font = ThemeManager.createFont(fontSize);
        const headerRectangle = this.sectorMap.headerRectangle;
        const leftOffset = 8;
        const headerPaddingX = 8;
        const signPaddingX = 5;
        const headerHeight = headerRectangle.Height;
        const selectedIndustry = this.sectorMap.selectedIndustry;
        if (this.sectorMap.isSelected || !isNullOrUndefined(selectedIndustry)) {
            const allWidth = gr.GetTextMetrics('All', font).width + 8;
            const allSize = { width: allWidth, height: headerHeight };
            const signWidth = gr.GetTextMetrics(this._arrowText, font).width + 2 * signPaddingX;
            this.headerAllRectangle = new Rectangle(headerRectangle.X + leftOffset, Math.round(headerRectangle.Y + headerRectangle.Height / 2 - allSize.height / 2), allSize.width + 2 * headerPaddingX, allSize.height);
            const headerWidth = gr.GetTextMetrics(this.areaName, font).width + 8;
            const headerTextSize = { width: headerWidth, height: headerHeight };
            this.headerAllSignRectangle = new Rectangle(this.headerAllRectangle.Right(), headerRectangle.Y, signWidth, headerRectangle.Height);
            this.headerTextRectangle = new Rectangle(this.headerAllSignRectangle.Right(), Math.round(headerRectangle.Y + headerRectangle.Height / 2 - headerTextSize.height / 2), headerTextSize.width + 2 * headerPaddingX, headerTextSize.height);
            if (!isNullOrUndefined(selectedIndustry)) {
                const industryWidth = gr.GetTextMetrics(this.getSelectedIndustryName(), font).width;
                this.selectorSignRectangle = new Rectangle(this.headerTextRectangle.Right(), headerRectangle.Y, signWidth, headerRectangle.Height);
                this.headerIndustryTextRectangle = new Rectangle(this.selectorSignRectangle.Right(), headerRectangle.Y, industryWidth + 2 * headerPaddingX, headerRectangle.Height);
            }
            else {
                this.selectorSignRectangle = Rectangle.Empty();
                this.headerIndustryTextRectangle = Rectangle.Empty();
            }
        }
        else {
            this.headerAllRectangle = Rectangle.Empty();
            this.headerAllSignRectangle = Rectangle.Empty();
            this.headerIndustryTextRectangle = Rectangle.Empty();
            const textWidth = gr.GetTextMetrics(this.areaName, font).width + 2 * headerPaddingX;
            this.headerTextRectangle = new Rectangle(headerRectangle.X + leftOffset, headerRectangle.Y + 1, Math.min(textWidth, headerRectangle.Width - leftOffset), headerRectangle.Height - 1); // We add 1 to Y and minus 1 from Height so that the stroke does not overlap the text
        }
    }
    getSelectedIndustryName() {
        const selectedIndustry = this.sectorMap.selectedIndustry;
        if (isNullOrUndefined(selectedIndustry)) {
            return '';
        }
        return isValidString(selectedIndustry.industryName)
            ? selectedIndustry.industryName.toUpperCase()
            : this._undefinedIndustryName.toUpperCase();
    }
}
HeatmapSectorRenderer.HOVERWIDTH = 1;
